import React, { useRef } from 'react';
import { useFrame } from '@react-three/fiber';

// Tool shapes are intentionally tiny + distinct so the job reads from above.
function Tool({ kind }) {
  switch (kind) {
    case 'shears':
      return (<mesh position={[0.11, 0.18, 0.04]}><boxGeometry args={[0.02, 0.1, 0.02]} /><meshStandardMaterial color="#cfd3d6" metalness={0.6} roughness={0.4} /></mesh>);
    case 'wateringCan':
      return (<mesh position={[0.12, 0.16, 0.04]}><cylinderGeometry args={[0.04, 0.04, 0.06, 8]} /><meshStandardMaterial color="#4a90d9" roughness={0.6} /></mesh>);
    case 'flask':
      return (<mesh position={[0.11, 0.18, 0.04]}><coneGeometry args={[0.04, 0.07, 8]} /><meshStandardMaterial color="#9fe6c8" transparent opacity={0.8} /></mesh>);
    case 'wrench':
      return (<mesh position={[0.11, 0.18, 0.04]} rotation={[0, 0, 0.4]}><boxGeometry args={[0.02, 0.11, 0.02]} /><meshStandardMaterial color="#b8bcc0" metalness={0.7} roughness={0.3} /></mesh>);
    case 'clipboard':
      return (<mesh position={[0.11, 0.18, 0.05]}><boxGeometry args={[0.06, 0.08, 0.01]} /><meshStandardMaterial color="#e8e2c8" roughness={0.8} /></mesh>);
    default:
      return null;
  }
}

function Hair({ style, color }) {
  if (style === 'bald') return null;
  const scaleY = style === 'afro' ? 1.0 : 0.55;
  const r = style === 'afro' ? 0.09 : 0.075;
  return (
    <mesh position={[0, 0.4, 0]} scale={[1, scaleY, 1]}>
      <sphereGeometry args={[r, 10, 8]} />
      <meshStandardMaterial color={color} roughness={0.9} />
    </mesh>
  );
}

/**
 * Static modular worker assembled from primitive parts.
 * Bob/facing/visibility are applied by the parent group in WorkerSprite; leg swing is read
 * imperatively from motionRef each frame so the model never re-renders.
 * @param {object} appearance - from workerAppearance()
 * @param {object} uniform - from workerUniform()
 * @param {{current:{legSwing:number}}} motionRef - shared mutable motion state written by parent
 */
export function WorkerModel({ appearance, uniform, motionRef }) {
  const { skin, hairStyle, hairColor, heightScale, build, hasBeard, hasGlasses, hasBackpack } = appearance;
  const torsoW = 0.18 * build;
  const legL = useRef();
  const legR = useRef();

  useFrame(() => {
    const swing = (motionRef && motionRef.current && motionRef.current.legSwing) || 0;
    if (legL.current) legL.current.rotation.x = swing;
    if (legR.current) legR.current.rotation.x = -swing;
  });

  return (
    <group scale={[1, heightScale, 1]}>
      {/* Legs (pivot at hip; swing driven imperatively via motionRef) */}
      <group ref={legL} position={[-0.05, 0.07, 0]}>
        <mesh position={[0, -0.07, 0]} castShadow><cylinderGeometry args={[0.04, 0.04, 0.14, 8]} /><meshStandardMaterial color={uniform.pants} roughness={0.8} /></mesh>
      </group>
      <group ref={legR} position={[0.05, 0.07, 0]}>
        <mesh position={[0, -0.07, 0]} castShadow><cylinderGeometry args={[0.04, 0.04, 0.14, 8]} /><meshStandardMaterial color={uniform.pants} roughness={0.8} /></mesh>
      </group>

      {/* Torso */}
      <mesh position={[0, 0.23, 0]} castShadow><boxGeometry args={[torsoW, 0.18, 0.1]} /><meshStandardMaterial color={uniform.shirt} roughness={0.7} /></mesh>

      {/* Arms */}
      <mesh position={[-(torsoW / 2 + 0.03), 0.23, 0]} rotation={[0, 0, 0.15]} castShadow><cylinderGeometry args={[0.03, 0.03, 0.15, 8]} /><meshStandardMaterial color={uniform.shirt} roughness={0.7} /></mesh>
      <mesh position={[torsoW / 2 + 0.03, 0.23, 0]} rotation={[0, 0, -0.15]} castShadow><cylinderGeometry args={[0.03, 0.03, 0.15, 8]} /><meshStandardMaterial color={uniform.shirt} roughness={0.7} /></mesh>

      {/* Head */}
      <mesh position={[0, 0.4, 0]} castShadow><sphereGeometry args={[0.07, 12, 10]} /><meshStandardMaterial color={skin} roughness={0.6} /></mesh>
      <Hair style={hairStyle} color={hairColor} />
      {hasBeard && (<mesh position={[0, 0.36, 0.05]}><sphereGeometry args={[0.05, 8, 8]} /><meshStandardMaterial color={hairColor} roughness={0.9} /></mesh>)}
      {hasGlasses && (<mesh position={[0, 0.41, 0.06]}><boxGeometry args={[0.09, 0.02, 0.01]} /><meshStandardMaterial color="#222" /></mesh>)}

      {/* Hard hat (uniform) */}
      {uniform.hardHat && (
        <group position={[0, 0.45, 0]}>
          <mesh><sphereGeometry args={[0.075, 12, 8, 0, Math.PI * 2, 0, Math.PI / 2]} /><meshStandardMaterial color="#f2c200" roughness={0.5} /></mesh>
          <mesh position={[0, 0, 0.04]}><boxGeometry args={[0.16, 0.01, 0.05]} /><meshStandardMaterial color="#f2c200" roughness={0.5} /></mesh>
        </group>
      )}

      {/* Backpack (accessory) */}
      {hasBackpack && (<mesh position={[0, 0.23, -0.07]} castShadow><boxGeometry args={[0.12, 0.14, 0.05]} /><meshStandardMaterial color="#5a4a3a" roughness={0.8} /></mesh>)}

      <Tool kind={uniform.tool} />
    </group>
  );
}
